/**
 * 大六壬盘面（Web）
 *
 * 分区：四柱月将 → 天地盘十二宫圆盘 → 四课 → 三传 → 课体 → 断课 → 天将布宫 → 神煞
 * 纯展示组件：入参仅 DaLiuRenResult，起课/存档由页面负责。
 */
import { TIAN_JIANG_DESC, type DaLiuRenResult, type Verdict } from '@suangua/shared';

/** 吉凶评级 → 徽章样式 */
function fortuneClass(f: string): string {
  return (
    {
      吉: 'border-jade-400/40 bg-jade-400/10 text-jade-400',
      中吉: 'border-gold-500/40 bg-gold-500/10 text-gold-400',
      平: 'border-line bg-fill text-t2',
      小凶: 'border-cinnabar-500/30 bg-cinnabar-500/8 text-cinnabar-500/85',
      凶: 'border-cinnabar-500/50 bg-cinnabar-500/12 text-cinnabar-500',
    }[f] ?? 'border-line bg-fill text-t2'
  );
}

/** 要点级别 → 圆点色 */
function levelDot(level: Verdict['level']): string {
  return level === '吉' ? 'bg-jade-400' : level === '凶' ? 'bg-cinnabar-500' : 'bg-t3';
}

/** 天将吉凶倾向（用于着金 / 朱） */
const JI_JIANG = new Set(['贵人', '青龙', '六合', '太常', '太阴', '天后']);
const XIONG_JIANG = new Set(['螣蛇', '白虎', '玄武', '天空']);

function jiangClass(jiang: string): string {
  if (JI_JIANG.has(jiang)) return 'text-gold-400';
  if (XIONG_JIANG.has(jiang)) return 'text-cinnabar-500/90';
  return 'text-t2';
}

export function DaLiuRenPanel({ result }: { result: DaLiuRenResult }) {
  const r = result;
  const siZhu = [
    ['年柱', r.siZhu.year.text],
    ['月柱', r.siZhu.month.text],
    ['日柱', r.siZhu.day.text],
    ['时柱', r.siZhu.hour.text],
  ] as const;

  /** 十二宫：地盘索引 i，天盘支 = r.pan.tianPan[i]，天将 = r.tianJiang[i] */
  const palaces = r.pan.diPan.map((di, i) => ({
    di,
    tian: r.pan.tianPan[i] ?? '',
    jiang: r.tianJiang[i]?.jiang ?? '',
  }));

  return (
    <div className="space-y-5">
      {/* ══ 四柱 · 月将 ══ */}
      <section className="ink-card p-5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] tracking-[.25em] text-t2">四 柱 · 月 将</span>
          <span className={`rounded-lg border px-2 py-0.5 text-[11px] ${fortuneClass(r.interpretation.fortune)}`}>
            {r.interpretation.fortune}（{r.interpretation.score >= 0 ? '+' : ''}
            {Math.round(r.interpretation.score)}）
          </span>
        </div>

        <div className="mt-3 grid grid-cols-4 gap-2">
          {siZhu.map(([label, text]) => (
            <div key={label} className="rounded-lg border border-line bg-fill px-2 py-2 text-center">
              <p className="text-[10px] text-t3">{label}</p>
              <p className="font-zh mt-1 text-[15px] font-semibold text-gold-400">{text}</p>
            </div>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
          <span className="chip-xs">
            月将 {r.yueJiang.zhi}
            {r.yueJiang.name}（{r.yueJiang.qi}后换将）
          </span>
          <span className="chip-xs">占时 {r.zhanShi}</span>
          <span className="chip-xs">
            {r.dayNight}占 · 贵人临{r.guiRenGong}宫 · {r.jiangDirection}布
          </span>
          <span className="chip-xs">
            {r.xun} · 空亡{r.kongWang.join('')}
          </span>
        </div>
      </section>

      {/* ══ 天地盘十二宫 ══ */}
      <section className="ink-card p-5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] tracking-[.25em] text-t2">天 地 盘</span>
          <span className="text-[11px] text-t3">{r.pan.layout}（外天盘 · 内地盘）</span>
        </div>

        <div className="mt-2 flex justify-center">
          <div className="relative h-[360px] w-[360px]">
            <div className="absolute inset-[26px] rounded-full border border-line" />
            <div className="absolute inset-[92px] rounded-full border border-gold-500/25" />

            {palaces.map((p, i) => {
              const angle = 180 + i * 30;
              return (
                <div
                  key={p.di}
                  className="absolute left-1/2 top-1/2 h-0 w-0"
                  style={{ transform: `rotate(${angle}deg) translateY(-150px) rotate(${-angle}deg)` }}
                >
                  <div className="-mt-[30px] -ml-[26px] w-[52px] rounded-lg border border-line bg-fill px-1 py-1 text-center">
                    <p className={`font-zh text-[13px] font-bold leading-tight ${jiangClass(p.jiang)}`}>{p.tian}</p>
                    <p className={`text-[9px] leading-tight ${jiangClass(p.jiang)}`}>{p.jiang}</p>
                    <p className="font-zh mt-0.5 text-[10px] leading-tight text-t3">{p.di}</p>
                  </div>
                </div>
              );
            })}

            {/* 盘心：课体 + 三传 */}
            <div className="absolute left-1/2 top-1/2 flex h-[132px] w-[132px] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border border-gold-500/30 bg-fill px-2 text-center">
              <p className="font-zh text-[13px] font-semibold text-gold-400">{r.keTi.name}</p>
              <p className="mt-1 font-zh text-[18px] font-bold tracking-[.1em] text-t1">
                {r.chuan.map((c) => c.zhi).join('')}
              </p>
              <p className="mt-0.5 text-[10px] text-t3">月将加时</p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ 四课 ══ */}
      <section className="ink-card p-5">
        <span className="text-[10px] tracking-[.25em] text-t2">四 课</span>
        <div className="mt-3 grid grid-cols-4 gap-2">
          {r.ke.map((k) => (
            <div key={k.index} className="rounded-lg border border-line bg-fill px-2 py-2 text-center">
              <p className="text-[10px] text-t3">{k.name}</p>
              <p className={`font-zh mt-1 text-[17px] font-bold leading-none ${jiangClass(k.tianJiang)}`}>
                {k.shang}
              </p>
              <p className={`mt-0.5 text-[10px] leading-tight ${jiangClass(k.tianJiang)}`}>{k.tianJiang}</p>
              <div className="my-1 border-t border-line" />
              <p className="font-zh text-[13px] leading-none text-t2">{k.xia}</p>
              <p className="mt-1 text-[10px] text-t3">
                {k.liuQin}
                {k.kong && <span className="text-cinnabar-500">·空</span>}
              </p>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-t3">{r.keTi.reason}</p>
      </section>

      {/* ══ 三传 ══ */}
      <section className="ink-card p-5">
        <div className="flex items-baseline justify-between">
          <span className="text-[10px] tracking-[.25em] text-t2">三 传</span>
          <span className="text-[11px] text-t3">{r.keTi.extras.join(' · ') || '—'}</span>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {r.chuan.map((c) => (
            <div key={c.position} className="rounded-lg border border-line bg-fill px-2 py-2 text-center">
              <p className="text-[10px] text-t3">{c.position}</p>
              <p className={`font-zh mt-1 text-[22px] font-bold leading-none ${jiangClass(c.tianJiang)}`}>
                {c.zhi}
              </p>
              <p className={`mt-0.5 text-[10px] leading-tight ${jiangClass(c.tianJiang)}`}>{c.tianJiang}</p>
              <p className="mt-1 text-[10px] text-t2">
                {c.liuQin}
                {c.kong && <span className="text-cinnabar-500">·空</span>}
              </p>
              <p className="mt-0.5 text-[10px] text-t3">
                遁{c.dunGan ?? '—'}
                {c.changSheng ? ` · ${c.changSheng}` : ''}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ══ 断课 ══ */}
      <section className="ink-card p-5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] tracking-[.25em] text-t2">断 课</span>
          <span className={`rounded-lg border px-2 py-0.5 text-[11px] ${fortuneClass(r.interpretation.fortune)}`}>
            {r.interpretation.summary}
          </span>
        </div>

        <p className="mt-3 text-[13px] leading-relaxed text-t1">{r.interpretation.overall}</p>

        <div className="mt-3 max-h-72 divide-y divide-line overflow-y-auto pr-1">
          {r.interpretation.points.map((p, i) => (
            <div key={`${p.category}-${i}`} className="flex items-start gap-2 py-2">
              <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${levelDot(p.level)}`} />
              <span className="w-10 shrink-0 pt-0.5 text-[10px] text-t3">{p.category}</span>
              <span className="min-w-0 flex-1 text-[12px] leading-relaxed text-t2">{p.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ══ 应期 ══ */}
      <section className="ink-card p-5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] tracking-[.25em] text-t2">应 期</span>
          <span className="text-[11px] text-t3">取用：{r.yingQi.basis}</span>
        </div>

        <p className="mt-2.5 text-[13px] leading-relaxed text-t1">{r.yingQi.text}</p>

        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {r.yingQi.monthLabel && <span className="chip-xs">应月 · 农历{r.yingQi.monthLabel}</span>}
          {r.yingQi.daysAhead !== null && (
            <span className="chip-xs">
              速应 {r.yingQi.daysAhead === 0 ? '即今日' : `${r.yingQi.daysAhead} 日后`}
              {r.yingQi.dayGanZhi ? `（${r.yingQi.dayGanZhi}日）` : ''}
            </span>
          )}
        </div>

        {r.yingQi.notes.length > 0 && (
          <ul className="mt-2 space-y-1">
            {r.yingQi.notes.map((n) => (
              <li key={n} className="text-[11px] leading-relaxed text-t3">
                · {n}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ══ 本命 · 行年（可选输入） ══ */}
      {r.benMing && (
        <section className="ink-card p-5">
          <span className="text-[10px] tracking-[.25em] text-t2">本 命 · 行 年</span>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-line bg-fill px-3 py-2">
              <p className="text-[10px] text-t3">本命（{r.benMing.birthYear} 年生）</p>
              <p className="font-zh mt-1 text-[20px] font-bold leading-none text-gold-400">{r.benMing.mingZhi}</p>
              <p className="mt-1 text-[11px] text-t2">
                上神 {r.benMing.mingShangShen} 乘{' '}
                <span className={jiangClass(r.benMing.mingJiang)}>{r.benMing.mingJiang}</span>
              </p>
            </div>
            <div className="rounded-lg border border-line bg-fill px-3 py-2">
              <p className="text-[10px] text-t3">
                行年（虚岁 {r.benMing.age} · {r.benMing.gender}）
              </p>
              <p className="font-zh mt-1 text-[20px] font-bold leading-none text-gold-400">{r.benMing.xingNian}</p>
              <p className="mt-1 text-[11px] text-t2">
                上神 {r.benMing.xingNianShangShen} 乘{' '}
                <span className={jiangClass(r.benMing.xingNianJiang)}>{r.benMing.xingNianJiang}</span> ·{' '}
                {r.benMing.xingNianLiuQin}
                {r.benMing.xingNianKong && <span className="text-cinnabar-500">·空</span>}
              </p>
            </div>
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-t3">
            行年上神为断事「类神」：男命 1 岁起丙寅顺行、女命 1 岁起壬申逆行，逐年递进一位。
          </p>
        </section>
      )}

      {/* ══ 天将布宫 ══ */}
      <section className="ink-card p-5">
        <span className="text-[10px] tracking-[.25em] text-t2">天 将 布 宫</span>
        <div className="mt-3 grid grid-cols-3 gap-1.5 sm:grid-cols-4">
          {r.tianJiang.map((t) => (
            <div
              key={t.gong}
              className="flex items-center justify-between rounded-lg border border-line bg-fill px-2 py-1.5 text-[11px]"
            >
              <span className="text-t3">{t.gong}</span>
              <span className={`font-zh ${jiangClass(t.jiang)}`}>
                {t.zhi}
                {t.jiang}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-t3">
          贵人为十二天将之首，{r.jiangDirection}布：{TIAN_JIANG_DESC['贵人']}。
        </p>
      </section>

      {/* ══ 神煞 ══ */}
      <section className="ink-card p-5">
        <span className="text-[10px] tracking-[.25em] text-t2">神 煞</span>
        <div className="mt-3 space-y-1.5">
          {r.shenSha.map((s) => (
            <div key={s.name} className="flex items-start gap-2 rounded-lg border border-line bg-fill px-2.5 py-1.5">
              <span className="font-zh w-12 shrink-0 text-[12px] text-gold-400">{s.name}</span>
              <span className="font-zh w-8 shrink-0 text-center text-[12px] text-t1">{s.value}</span>
              <span className="w-24 shrink-0 text-[10px] text-t3">
                {s.basis}
                {s.inChuan && <span className="ml-1 text-jade-400">入传</span>}
                {!s.inChuan && s.inKe && <span className="ml-1 text-gold-400">临课</span>}
              </span>
              <span className="min-w-0 flex-1 text-[11px] leading-relaxed text-t2">{s.desc}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
